import {
  Component,
  Host,
  Prop,
  Element,
  State,
  Event,
  EventEmitter,
  h,
  Watch,
} from '@stencil/core';
import { assignLanguage, observerConfig, emitEvent } from '../../utils/utils';
import I18N from './i18n/I18N';

/**
 * Search is a space for entering keywords to find relevant information.
 */
@Component({
  tag: 'gcds-search',
  styleUrl: 'gcds-search.css',
  shadow: true,
})
export class GcdsSearch {
  @Element() el: HTMLElement;

  /**
   * Set the placeholder and label for the search input. Becomes "Search [placeholder]"
   */
  @Prop() placeholder: string = 'Canada.ca';

  /**
   * Sets the action for the search form. Default will be canada.ca global search
   */
  @Prop() action: string = '/sr/srb.html';

  /**
   * Set the form method of the search form
   */
  @Prop() method: 'get' | 'post' = 'get';

  /**
   * Set the name of the search input
   */
  @Prop() name: string = 'q';

  /**
   * Set the id of the search input
   */
  @Prop() searchId: string = 'search';

  /**
   * Set the value of the search input
   */
  @Prop({ mutable: true }) value: string;

  /**
   * Set a list of predefined search terms
   */
  @Prop({ mutable: true }) suggested: string[] | string;
  @Watch('suggested')
  watchSuggestedHandler() {
    if (this.suggested == null || this.suggested.length === 0) {
      this.suggestedArray = null;
      return;
    }

    if (typeof this.suggested === 'string') {
      try {
        this.suggested = JSON.parse(this.suggested as string);
      } catch (e) {
        console.error(
          'gcds-search: suggested prop is not valid JSON string',
          e,
        );
        this.suggestedArray = null;
        return;
      }
    }

    this.suggestedArray = this.suggested as string[];
  }

  /**
   * Events
   */

  /**
   * Emitted when the search element has received input.
   */
  @Event() gcdsInput!: EventEmitter<string>;

  /**
   * Emitted when the search input value has changed.
   */
  @Event() gcdsChange!: EventEmitter<string>;

  /**
   * Emitted when the search input has gained focus.
   */
  @Event() gcdsFocus!: EventEmitter<void>;

  /**
   * Emitted when the search input has lost focus.
   */
  @Event() gcdsBlur!: EventEmitter<void>;

  /**
   * Emitted when the search form has submitted.
   */
  @Event() gcdsSubmit!: EventEmitter<string>;

  /**
   * Language of rendered component
   */
  @State() lang: string;

  /**
   * Array of suggested search terms
   */
  @State() suggestedArray: string[] = null;

  private handleInput = (e, customEvent) => {
    const input = e.target as HTMLInputElement;
    this.value = input.value;

    customEvent.emit(this.value);
  };

  /*
   * Observe lang attribute change
   */
  updateLang() {
    const observer = new MutationObserver(mutations => {
      if (mutations[0].oldValue != this.el.lang) {
        this.lang = this.el.lang;
      }
    });
    observer.observe(this.el, observerConfig);
  }

  async componentWillLoad() {
    // Define lang attribute
    this.lang = assignLanguage(this.el);

    this.watchSuggestedHandler();

    // Observe lang attribute changes
    this.updateLang();
  }

  render() {
    const {
      placeholder,
      action,
      method,
      name,
      value,
      lang,
      searchId,
      suggestedArray,
    } = this;

    const labelText = `${I18N[lang].searchLabel.replace('{$}', placeholder)}`;

    const attrsInput = {
      name,
      placeholder: labelText,
    };

    const formAction =
      action === '/sr/srb.html'
        ? `https://www.canada.ca/${lang}/sr/srb.html`
        : action;

    return (
      <Host>
        <section class="gcds-search">
          <gcds-sr-only tag="h2">{I18N[lang].search}</gcds-sr-only>
          <form
            action={formAction}
            method={method}
            role="search"
            onSubmit={e => emitEvent(e, this.gcdsSubmit, this.value)}
            class="gcds-search__form"
          >
            <gcds-label
              label={labelText}
              label-for={searchId}
              hide-label
            ></gcds-label>
            <input
              type="search"
              id={searchId}
              {...(suggestedArray ? { list: 'search-list' } : {})}
              size={34}
              maxLength={170}
              onInput={e => this.handleInput(e, this.gcdsInput)}
              onChange={e => this.handleInput(e, this.gcdsChange)}
              onFocus={() => this.gcdsFocus.emit()}
              onBlur={() => this.gcdsBlur.emit()}
              {...attrsInput}
              class="gcds-search__input"
              value={value}
            ></input>

            {suggestedArray && (
              <datalist id="search-list">
                {suggestedArray.map((k, v) => (
                  <option value={k} key={v} />
                ))}
              </datalist>
            )}

            <gcds-button
              type="submit"
              class="gcds-search__button"
              exportparts="button"
            >
              <gcds-icon
                name="search"
                label={I18N[lang].search}
                size="h3"
              ></gcds-icon>
            </gcds-button>
          </form>
        </section>
      </Host>
    );
  }
}
